import { type ChangeEvent, type FormEvent, useCallback, useMemo, useState } from 'react'
import DialogButton from '../../../Common/Buttons/DialogButton'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogActions from '../../../Common/Dialog/DialogActions'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useTranslation } from 'react-i18next'
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material'
import SelectV2 from '../../../Common/Form/SelectV2'
import { CreateUserTaxNumber } from '../../../UserData/_actions/UserTaxNumberActions'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import SelectAddressDialog from '../Addresses/SelectAddressDialog'

interface Props {
  opened: boolean
  onClose: () => void
}

export default function AddTaxNumberDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const addresses = useAppSelector(state => state.get('userData').get('addresses').get('data'))
  const countries = useAppSelector(state => state.get('appData').get('countries'))

  const [type, setType] = useState('')
  const [address, setAddress] = useState(0)
  const [taxNumber, setTaxNumber] = useState('')
  const [addressDialogOpened, setAddressDialogOpened] = useState(false)

  const resetDialogState = useCallback(() => {
    setType('')
    setAddress(0)
    setTaxNumber('')
    setAddressDialogOpened(false)
  }, [])

  const onClose = useCallback(() => {
    props.onClose()
  }, [props.onClose])

  const selectedAddress = useMemo(() => {
    return addresses.get(String(address))
  }, [address, addresses])

  const addressLine1 = useMemo(() => {
    if (!selectedAddress) return ''

    return [
      selectedAddress.get('address'),
      selectedAddress.get('address2')
    ].filter(Boolean).join(', ')
  }, [selectedAddress])

  const addressLine2 = useMemo(() => {
    if (!selectedAddress) return ''

    const country = countries.get(String(selectedAddress.get('country')))

    return [
      selectedAddress.get('city'),
      selectedAddress.get('postal_code'),
      selectedAddress.get('province'),
      country?.get('name')
    ].filter(Boolean).join(', ')
  }, [countries, selectedAddress])

  const onChangeType = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value)
  }, [])

  const onChangeTaxNumber = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTaxNumber(e.target.value)
  }, [])

  const onAddressDialogOpen = useCallback(() => {
    setAddressDialogOpened(true)
  }, [])

  const onAddressDialogClose = useCallback(() => {
    setAddressDialogOpened(false)
  }, [])

  const onSelectAddress = useCallback((id: number) => {
    setAddress(id)
    onAddressDialogClose()
  }, [onAddressDialogClose])

  const onSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()

    const fd = new FormData()
    fd.append('type', type)
    fd.append('id_address', String(address))
    fd.append('tax_number', taxNumber)

    dispatch(CreateUserTaxNumber(fd).set({
      onSuccess: () => {
        onClose()
      }
    }))
  }, [address, dispatch, onClose, taxNumber, type])

  const valid = useMemo(() => {
    return type !== '' && address > 0 && taxNumber !== ''
  }, [type, address, taxNumber])

  return <Dialog
    open={props.opened}
    maxWidth="xs"
    fullWidth
    onClose={onClose}
    TransitionProps={{
      onExited: resetDialogState,
    }}
    showCloseButton
  >
    <form onSubmit={onSubmit}>
      <DialogTitle>{t('Add Tax number')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1" component="p">{t('You\'ll need to add your business address to your account first to be able to link it to your new tax number.')}</Typography>

          <SelectV2
            label={t('Type')}
            value={type}
            onChange={onChangeType}
          >
            <MenuItem value="uk-vat">{t('UK VAT')}</MenuItem>
            <MenuItem value="eu-vat">{t('EU VAT')}</MenuItem>
            <MenuItem value="canada-sk-pst">{t('Saskatchewan PST')}</MenuItem>
            <MenuItem value="canada-bc-pst">{t('British Columbia PST')}</MenuItem>
          </SelectV2>

          <Stack spacing={1}>
            <Typography variant="body2">{t('Address')}</Typography>

            {selectedAddress ? <Stack spacing={0.25}>
              <Typography variant="body2">{selectedAddress.get('name')}</Typography>
              {addressLine1 !== '' ? <Typography variant="caption">{addressLine1}</Typography> : null}
              {addressLine2 !== '' ? <Typography variant="caption">{addressLine2}</Typography> : null}
            </Stack> : <Typography variant="caption" color="text.secondary">
              {t('No address selected')}
            </Typography>}

            <Button
              color="secondary"
              variant="outlined"
              onClick={onAddressDialogOpen}
              sx={{
                alignSelf: 'flex-start'
              }}
            >{t('Select Address')}</Button>
          </Stack>

          <TextField
            value={taxNumber}
            onChange={onChangeTaxNumber}
            label={t('Tax number')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton
          color="secondary"
          onClick={onClose}
        >{t('Cancel')}</DialogButton>
        <DialogButton
          color="primary"
          type="submit"
          disabled={!valid}
        >{t('Save')}</DialogButton>
      </DialogActions>
    </form>

    <SelectAddressDialog
      opened={addressDialogOpened}
      onClose={onAddressDialogClose}
      onSelect={onSelectAddress}
      emptyStateDescription={t('Add your business address to link it to your tax number.')}
    />
  </Dialog>
}
